import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import { ApiErrorState } from '@/components/states/ApiErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { type } from '@/theme/typography';

import { useSession } from './session';

/**
 * The gate every authenticated screen sits behind.
 *
 * An unauthenticated visitor goes to sign-in **with the address they were trying to reach**, so
 * signing in lands them where they meant to be rather than on a default screen. This is the
 * behaviour BUG-013 deliberately left undecided: the redirect is a screen decision (`DESIGN.md`
 * §6.1), and this is the screen layer.
 *
 * ⚠️ **"Unauthenticated" is a 401 and nothing else** (BUG-103). This read `if (session.isError)`,
 * so every way `GET /api/v1/auth/me/` could fail became the same sentence — *your session ended* —
 * including the one where the session is perfectly fine and the server is simply not there.
 * Measured against CI-built images with `web` stopped and nginx answering **502**: the app sent the
 * reader to `/sign-in`, and with `web` back the same session resumed without re-authentication, so
 * it was never invalid, only unreachable. The reader who follows the screen's invitation and signs
 * in again meets the same 502 on the login request and learns nothing.
 *
 * ⚠️ **And the unreachable branch discards nothing.** It renders a state and refetches; it does not
 * navigate, does not touch the cache and does not call `readerChanged`, which stays anchored on the
 * 401 in `queryClient.ts`. That is deliberate and it is the expensive half of this project's
 * history: BUG-045 and BUG-066 are both doors that threw away a live reader's state, and here the
 * reader has not changed at all — the client is holding exactly the right thing and the only thing
 * missing is an answer. Emptying anything on an outage would turn a two-minute deploy into a
 * re-authentication for everybody who had a tab open.
 *
 * The three cases, decided here and in `SignIn` between them:
 *
 * | what came back | what the reader gets |
 * |---|---|
 * | 401 on an ordinary request | sign-in, with the address they were on |
 * | 401 on the sign-in request itself | that form's own message, above the form (§6.1) — never a redirect |
 * | 502/503/504, or no answer at all | *the server is unreachable*, a Retry, and the session untouched |
 *
 * ⚠️ **A reader who is already inside the app meets none of this, and that is measured rather than
 * hoped for.** The third row is the *cold* case. With a session in the cache a failed background
 * refetch leaves `session.data` in place, and the gate renders the children off it — so an outage
 * that begins while somebody is halfway through an interview takes nothing away from them, which is
 * §6.6's *"the interview screen must survive it"*. ⚠️ It is a **guard**, not an accident of the
 * client: measured before it was written, TanStack's tracked-property notification does not
 * re-render this gate at the moment such a refetch fails, so the screen survived — but the observer
 * result is `isError` with `isRefetchError` and its data intact, so the **next render from any
 * cause** (a keystroke, a toast, the autosave indicator ticking) would have torn the screen down.
 * Surviving an outage cannot depend on nothing else re-rendering for the length of it.
 *
 * A **hung** request is a fourth case and needs nothing: it never resolves, so the query stays
 * pending and the skeleton below stays on screen, which is what a live pass measured as correct
 * while `web` was merely paused. Only the *refused* connection was ever wrong.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const { t } = useTranslation(['common', 'auth']);

  if (session.isPending) {
    // Shaped like the shell it is about to become, never a centred spinner (§8).
    return (
      <div className="flex min-h-screen">
        <Skeleton className="h-screen w-60" />
        <div className="flex-1 space-y-3 px-8 py-6">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  if (session.isError) {
    if (session.error.isUnauthenticated) {
      return (
        <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
      );
    }

    /*
     * Everything else `me/` can answer: the gateway statuses above, a dropped connection, and a 500
     * — the application saying it broke is still not the application saying who the reader is. None
     * of them is evidence that the session ended, so none of them may be rendered as though it
     * were.
     *
     * ⚠️ **And it is only rendered when there is no session to fall back on.** `session.data` holds
     * the last answer `me/` gave, and a failed *refetch* does not withdraw it: the reader is still
     * whoever it says, the server simply cannot be asked again just now. So a reader already inside
     * the app keeps their screen — §6.6's *"the interview screen must survive it"*, held at the gate
     * rather than hoped for — and this state is reached on a **cold load or a hard reload during the
     * outage**, where there is nothing to keep. The 401 above is deliberately not subject to this:
     * an ended session withdraws the answer whether or not the cache still remembers it.
     *
     * `ApiErrorState` picks the sentence; the Retry is the reader's own, because nothing here
     * retries by itself (the session query is `retry: false`, so a 502 is one request and one
     * state, not a loop against a server that is already struggling).
     *
     * It is centred on the page rather than framed by `AppShell`: the shell names the reader and
     * their tenant off a session this branch does not have. ⚠️ **Which is why the page carries its
     * own `h1`** — the shell's title is gone with it, so without one the document would have no
     * heading at all and a screen reader arriving here would have nothing to orient by (§9). That is
     * `RouteError`'s finding from a live pass on 2026-07-25, and this branch is the one full-page
     * surface `states.test.tsx`'s census structurally cannot see: it derives itself from the route
     * table and skips every route that has children, because *"A route with children is a layout
     * (the session guard and the shell), not a screen."* — this guard being one of the two it
     * names. The heading is pinned in `serverUnreachable.test.tsx` instead, over every branch this
     * page can render. The title sits *above* `ApiErrorState` rather than being passed into it, so
     * every state it can render — the unreachable sentence, the offline banner, a refusal — is
     * under a heading; `OfflineState` is a banner and has none of its own.
     */
    if (session.data === undefined) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-paper-bg px-8">
          <div className="w-full max-w-md">
            <h1 className={`${type.pageTitle} mb-4`}>
              {t('common:appUnavailableTitle', { product: t('auth:product') })}
            </h1>
            <ApiErrorState error={session.error} onRetry={() => void session.refetch()} />
          </div>
        </main>
      );
    }
  }

  return <>{children}</>;
}
